// =============================================================================
// Origin UI — src/pages/approvals/ApprovalReviewPage.tsx
// Full request review screen for approvers.
// Shows request details, selections, comments, approval history.
// Actions: Approve (optional comment), Return for Changes (mandatory comment),
// Cancel (available to approver per RBAC spec).
// =============================================================================

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { requestApi, approvalApi } from '@/services/requestApi';
import {
  StatusBadge, LoadingSpinner, Card, PageHeader,
  ErrorBanner, ConfirmDialog,
} from '@/components/common';
import type { RequestDetail } from '@/types/api';
import styles from './approvals.module.css';

export function ApprovalReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [request, setRequest] = useState<RequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Approve dialog state
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approveComment, setApproveComment] = useState('');

  // Return dialog state
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnComment, setReturnComment] = useState('');
  const [returnError, setReturnError] = useState('');

  // Cancel dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    if (id) loadRequest(id);
  }, [id]);

  async function loadRequest(requestId: string) {
    try {
      setLoading(true);
      const data = await requestApi.getById(requestId);
      setRequest(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load request');
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove() {
    if (!id) return;
    try {
      setSubmitting(true);
      setError('');
      await approvalApi.approve(id, approveComment.trim() || undefined);
      setApproveDialogOpen(false);
      navigate('/approvals');
    } catch (err: any) {
      const body = err.body ? JSON.parse(err.body) : null;
      setError(body?.error || 'Failed to approve request');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleReturn() {
    if (!id) return;
    if (!returnComment.trim()) {
      setReturnError('A comment is required when returning for changes.');
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      setReturnError('');
      await approvalApi.returnForChanges(id, returnComment.trim());
      setReturnDialogOpen(false);
      navigate('/approvals');
    } catch (err: any) {
      const body = err.body ? JSON.parse(err.body) : null;
      setReturnError(body?.error || 'Failed to return request');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCancel() {
    if (!id) return;
    try {
      setSubmitting(true);
      await requestApi.cancel(id, { reason: cancelReason || undefined });
      setCancelDialogOpen(false);
      navigate('/approvals');
    } catch (err: any) {
      const body = err.body ? JSON.parse(err.body) : null;
      setError(body?.error || 'Failed to cancel request');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <LoadingSpinner />;
  if (!request) return <ErrorBanner message="Request not found" />;

  const r = request;

  return (
    <div>
      <PageHeader
        title={`Review: ${r.fullName}`}
        subtitle={`${r.requestType} · ${r.oqtEmployeeId} · Submitted by ${r.createdByDisplayName}`}
        action={<StatusBadge status={r.status} />}
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      {/* Decision Action Bar — only shown for Submitted status */}
      {r.status === 'Submitted' && (r.actions.canApprove || r.actions.canReturn) && (
        <div className={styles.decisionBar}>
          <div className={styles.decisionLabel}>Your decision:</div>
          <div className={styles.decisionActions}>
            {r.actions.canApprove && (
              <button
                className={styles.btnApprove}
                onClick={() => setApproveDialogOpen(true)}
                disabled={submitting}
              >
                ✓ Approve
              </button>
            )}
            {r.actions.canReturn && (
              <button
                className={styles.btnReturn}
                onClick={() => setReturnDialogOpen(true)}
                disabled={submitting}
              >
                ↩ Return for Changes
              </button>
            )}
            {r.actions.canCancel && (
              <button
                className={styles.btnCancelAction}
                onClick={() => setCancelDialogOpen(true)}
                disabled={submitting}
              >
                Cancel Request
              </button>
            )}
          </div>
        </div>
      )}

      {/* Non-submitted info banner */}
      {r.status !== 'Submitted' && (
        <div className={styles.infoBanner}>
          This request is in <strong>{r.status}</strong> status and is not awaiting your decision.
          {r.status === 'Returned' && ' It has been returned to the requester for changes.'}
        </div>
      )}

      <div className={styles.reviewLayout}>
        {/* Main content */}
        <div className={styles.reviewMain}>
          {/* Employee Details */}
          <Card className={styles.section}>
            <h3 className={styles.sectionTitle}>Employee Details</h3>
            <div className={styles.detailGrid}>
              <DetailField label="Employee ID" value={r.oqtEmployeeId} />
              <DetailField label="Full Name" value={r.fullName} />
              <DetailField label="Email" value={r.oqtEmailAddress} />
              <DetailField label="Designation" value={r.designation} />
              <DetailField label="Department" value={r.department} />
              <DetailField label="Office Location" value={r.officeLocation} />
              <DetailField label="Line Manager" value={r.lineManagerEmail} />
              <DetailField label="Effective Date" value={new Date(r.effectiveDate).toLocaleDateString()} />
              {r.preferredInitials && <DetailField label="Initials" value={r.preferredInitials} />}
            </div>
          </Card>

          {/* Selections */}
          {r.selections.length > 0 && (
            <Card className={styles.section}>
              <h3 className={styles.sectionTitle}>
                {r.requestType === 'Onboarding' ? 'Requested Items' : 'Items to Revoke/Collect'}
                {' '}({r.selections.length})
              </h3>
              <div className={styles.selectionsList}>
                {r.selections.map(s => (
                  <div key={s.selectionId} className={styles.selectionItem}>
                    <span className={styles.selectionType}>{s.selectionType}</span>
                    <span className={styles.selectionValue}>{s.selectionValue}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Previous Approval History (for return loops) */}
          {r.approvals.length > 0 && (
            <Card className={styles.section}>
              <h3 className={styles.sectionTitle}>Approval History ({r.approvals.length})</h3>
              <div className={styles.approvalHistory}>
                {r.approvals.map(a => (
                  <div key={a.approvalId} className={styles.approvalItem}>
                    <div className={styles.approvalHeader}>
                      <StatusBadge status={a.decision === 'Approved' ? 'Approved' : 'Returned'} />
                      <span className={styles.approvalBy}>{a.approverDisplayName}</span>
                      <span className={styles.approvalDate}>
                        {new Date(a.decisionTimestamp).toLocaleString()}
                      </span>
                    </div>
                    {a.comment && <p className={styles.approvalComment}>{a.comment}</p>}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Comments */}
          <Card className={styles.section}>
            <h3 className={styles.sectionTitle}>Comments ({r.comments.length})</h3>
            {r.comments.length > 0 ? (
              <div className={styles.commentsList}>
                {r.comments.map(c => (
                  <div key={c.commentId} className={styles.commentItem}>
                    <div className={styles.commentHeader}>
                      <span className={styles.commentAuthor}>{c.authorDisplayName}</span>
                      <span className={styles.commentRole}>{c.authorRole}</span>
                      <span className={styles.commentDate}>
                        {new Date(c.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <p className={styles.commentText}>{c.commentText}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className={styles.muted}>No comments yet.</p>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className={styles.reviewSidebar}>
          <Card>
            <h4 className={styles.sidebarTitle}>Request Summary</h4>
            <div className={styles.summaryMeta}>
              <div><span>Request Type</span><span>{r.requestType}</span></div>
              <div><span>Status</span><StatusBadge status={r.status} /></div>
              <div><span>Created By</span><span>{r.createdByDisplayName}</span></div>
              <div><span>Created</span><span>{new Date(r.createdAt).toLocaleDateString()}</span></div>
              {r.submittedAt && (
                <div><span>Submitted</span><span>{new Date(r.submittedAt).toLocaleDateString()}</span></div>
              )}
              <div><span>Selections</span><span>{r.selections.length} item{r.selections.length !== 1 ? 's' : ''}</span></div>
              <div><span>ID</span><span className={styles.muted}>{r.requestId.slice(0, 8)}…</span></div>
            </div>
          </Card>

          {/* Tasks (if already generated) */}
          {r.tasks.length > 0 && (
            <Card>
              <h4 className={styles.sidebarTitle}>Tasks ({r.tasks.length})</h4>
              <div className={styles.taskMiniList}>
                {r.tasks.map(t => (
                  <div key={t.taskId} className={styles.taskMiniItem}>
                    <span>{t.taskType}</span>
                    <StatusBadge status={t.status} />
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* ─── Approve Dialog ─── */}
      <ConfirmDialog
        open={approveDialogOpen}
        title="Approve Request"
        message={`Approve the ${r.requestType.toLowerCase()} request for ${r.fullName}? This will generate IT tasks for execution.`}
        confirmLabel="Approve"
        confirmVariant="primary"
        onConfirm={handleApprove}
        onCancel={() => setApproveDialogOpen(false)}
      >
        <div className={styles.dialogField}>
          <label className={styles.dialogLabel}>Comment (optional)</label>
          <textarea
            className={styles.dialogTextarea}
            value={approveComment}
            onChange={e => setApproveComment(e.target.value)}
            placeholder="Any notes for the team..."
            rows={3}
          />
        </div>
      </ConfirmDialog>

      {/* ─── Return for Changes Dialog ─── */}
      {returnDialogOpen && (
        <div className={styles.overlay} onClick={() => setReturnDialogOpen(false)}>
          <div className={styles.dialog} onClick={e => e.stopPropagation()}>
            <h3 className={styles.dialogTitle}>Return for Changes</h3>
            <p className={styles.dialogMessage}>
              This request will be returned to {r.createdByDisplayName} for corrections.
              You must provide a comment explaining what needs to be fixed.
            </p>
            {returnError && (
              <div className={styles.dialogError}>{returnError}</div>
            )}
            <div className={styles.dialogField}>
              <label className={styles.dialogLabel}>
                What needs to be changed? <span className={styles.required}>*</span>
              </label>
              <textarea
                className={`${styles.dialogTextarea} ${returnError && !returnComment.trim() ? styles.dialogTextareaError : ''}`}
                value={returnComment}
                onChange={e => { setReturnComment(e.target.value); setReturnError(''); }}
                placeholder="Please describe the corrections needed..."
                rows={4}
                autoFocus
              />
            </div>
            <div className={styles.dialogActions}>
              <button className={styles.btnSecondary} onClick={() => setReturnDialogOpen(false)}>
                Cancel
              </button>
              <button
                className={styles.btnReturn}
                onClick={handleReturn}
                disabled={submitting}
              >
                {submitting ? 'Returning...' : 'Return for Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Cancel Dialog ─── */}
      <ConfirmDialog
        open={cancelDialogOpen}
        title="Cancel Request"
        message="Are you sure you want to cancel this request? This action is permanent."
        confirmLabel="Cancel Request"
        confirmVariant="danger"
        onConfirm={handleCancel}
        onCancel={() => setCancelDialogOpen(false)}
      >
        <div className={styles.dialogField}>
          <label className={styles.dialogLabel}>Reason (optional)</label>
          <textarea
            className={styles.dialogTextarea}
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
            placeholder="Why is this request being cancelled?"
            rows={3}
          />
        </div>
      </ConfirmDialog>
    </div>
  );
}

// ─── Sub-components ───

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.detailField}>
      <span className={styles.detailLabel}>{label}</span>
      <span className={styles.detailValue}>{value}</span>
    </div>
  );
}
